import { Bot, Loader2, RefreshCw } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/useToast";
import { useRunPullRequestReview } from "@/hooks/queries/use-pull-requests";
import { structuredAgentReview, type AgentReviewBody, type PullRequestView } from "@/lib/api/pull-requests";
import { apiErrorInfo } from "./pull-request-status";

const RECOMMENDATION: Record<AgentReviewBody["recommendation"], { label: string; className: string }> = {
  approve: { label: "Approve", className: "bg-green-600 text-white border-transparent" },
  needs_changes: { label: "Needs changes", className: "bg-amber-500 text-white border-transparent" },
  needs_human: { label: "Needs a human look", className: "bg-blue-600 text-white border-transparent" },
};

const SEVERITY_CLASS = {
  high: "text-red-600",
  medium: "text-amber-600",
  low: "text-muted-foreground",
} as const;

export function AgentReviewCard({ pr }: { pr: PullRequestView }) {
  const run = useRunPullRequestReview(pr.id);
  const { toast } = useToast();
  const envelope = pr.agentReview;
  const body = envelope ? structuredAgentReview(envelope.review) : null;
  const stale = envelope?.stale ?? false;

  const handleRun = async () => {
    try {
      await run.mutateAsync();
      toast("Review updated");
    } catch (err) {
      toast(apiErrorInfo(err).message);
    }
  };

  return (
    <section className="rounded-lg border p-4 space-y-3" aria-label="Agent review">
      <header className="flex flex-wrap items-center gap-2">
        <Bot className="h-4 w-4" />
        <h2 className="text-sm font-semibold">Agent review</h2>
        {body && (
          <Badge className={RECOMMENDATION[body.recommendation].className}>
            {RECOMMENDATION[body.recommendation].label}
          </Badge>
        )}
        {stale && (
          <Badge variant="outline" className="border-amber-500 text-amber-600">
            Stale — new commits since this review
          </Badge>
        )}
        <div className="ml-auto">
          <Button
            size="sm"
            variant="outline"
            disabled={run.isPending || pr.state !== "open"}
            onClick={() => { void handleRun(); }}
          >
            {run.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            {envelope ? "Run review again" : "Run review"}
          </Button>
        </div>
      </header>

      {!envelope && <p className="text-sm text-muted-foreground">No agent review yet.</p>}

      {envelope && !body && (
        <p className="text-sm whitespace-pre-wrap">
          {typeof envelope.review === "string" ? envelope.review : "This review can't be shown."}
        </p>
      )}

      {body && (
        <div className="space-y-3 text-sm">
          <p>{body.summary}</p>
          {body.reasons.length > 0 && (
            <ul className="list-disc pl-5 text-muted-foreground">
              {body.reasons.map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
          )}
          {body.risks.length > 0 && (
            <div>
              <h3 className="font-medium">Risks</h3>
              <ul className="space-y-1 mt-1">
                {body.risks.map((risk, i) => (
                  <li key={i}>
                    <span className={`font-medium capitalize ${SEVERITY_CLASS[risk.severity]}`}>{risk.severity}</span>
                    {risk.file && (
                      <span className="font-mono text-xs text-muted-foreground">
                        {" "}
                        {risk.file}
                        {risk.line ? `:${String(risk.line)}` : ""}
                      </span>
                    )}
                    <span> — {risk.explanation}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {body.testResults && (
            <div>
              <h3 className="font-medium">Tests</h3>
              <p className="text-muted-foreground">{body.testResults}</p>
            </div>
          )}
        </div>
      )}

      {envelope?.reviewedAt && (
        <p className="text-xs text-muted-foreground">
          Reviewed {formatDistanceToNow(new Date(envelope.reviewedAt), { addSuffix: true })}
          {envelope.model ? ` by ${envelope.model}` : ""}
        </p>
      )}
    </section>
  );
}
